import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatList, MatListItem } from '@angular/material/list';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { PERMISSION } from 'utils/permissions/permission-catalog';
import { AccessStore } from '../../stores/access.store';
import { PrefixViewPreferenceService } from '../../services/prefix-view-preference.service';
import { ModuleStore } from '../../stores/module.store';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { ModuleTypeNameEnum } from 'utils/enums/module-type-name.enum';
import { UserStore } from '../../stores/user.store';
import {
  isFeatureEnabled,
  FEATURE_FLAGS,
} from '../../shared/utils/feature-flags';
import { ReportStore } from '../../stores/report.store';
import { loadWhenPermitted } from '../../shared/access/load-when-permitted';
import {
  holdsRequirement,
  type RequiredPermission,
} from '../../shared/access/permission-requirement';
import { ThemeService } from '../../services/theme.service';
import { MenuService } from '../../services/menu.service';
import { SidenavUiService } from '../../services/sidenav-ui.service';
import { ContextFilterService } from '../../services/context-filter.service';
import { ModuleWithModuleType } from 'utils/interfaces/prisma-combined-types.util';
import { moduleIconName } from 'utils/constants/menu-icons.util';
import {
  BUILTIN_MENU_GROUP_KEYS,
  type CustomMenu,
  type MenuLayoutEntry,
} from 'utils/interfaces/menu.util';
import {
  mapBucketToNavCategory,
  resolveMenuBucket,
} from 'utils/functions/menu-placement.util';
import { switchMap } from 'rxjs';

interface NavigationItem {
  link: string;
  text: string;
  /**
   * Catalog permission(s) that light the item up (roles overhaul §11.2),
   * evaluated in the active org; an array means ALL of them. Omitted =
   * visible to every signed-in member. A `{ key, orgWide }` entry pins one
   * key's scope regardless of the item-level default below.
   */
  requiredPermission?: RequiredPermission | readonly RequiredPermission[];
  /**
   * Default scope for bare keys in `requiredPermission`: org-wide only (base
   * role ∪ grants), so a module binding does not light up a page that spans
   * the org (§7.6).
   */
  orgWide?: boolean;
  /**
   * Environment feature flag (FEATURE_FLAGS) that must be on for the item to
   * show; checked before permissions. The item's route carries the same flag
   * via `featureFlagGuard`, so a hidden item is never a reachable URL.
   */
  featureFlag?: string;
  /** Platform-operation item — shown only to global ubers, never role-driven. */
  uberOnly?: boolean;
  iconPath: string;
  matIconName?: string;
  moduleCategory: string;
  weight: number;
}

/** A custom menu group plus its visible nav items and expand state. */
interface CustomMenuGroupView {
  menu: CustomMenu;
  items: NavigationItem[];
  expanded: boolean;
}

type MenuVisibility = {
  [key: string]: {
    hidden: boolean;
    menu: boolean;
    hiddenGroup: boolean;
    chatHidden?: boolean;
    knowledgeHidden?: boolean;
  };
};

/**
 * Icons shown for each collapsible built-in group in the collapsed rail, where
 * the group's text header has no room. Every value is reserved in
 * DEFAULT_MENU_ICON_NAMES so a user can never pick the same glyph for a module
 * and make a group indistinguishable from an item.
 */
const BUILTIN_GROUP_ICONS: Record<string, string> = {
  ai: 'smart_toy',
  myDomain: 'apps',
  assetMGR: 'workspaces',
  myAudit: 'rule_folder',
  // System inherits the shield glyph (it's the platform-admin area now);
  // Admin gets 'tune' — it holds org-context configuration.
  admin: 'tune',
  reports: 'lab_profile',
  system: 'admin_panel_settings',
};

@Component({
  selector: 'gcp-workspace-sidenav',
  imports: [
    MatButton,
    MatListItem,
    MatList,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    MatDivider,
    CommonModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent {
  private readonly destroyRef = inject(DestroyRef);
  accessStore = inject(AccessStore);
  private readonly prefixViewPreference = inject(PrefixViewPreferenceService);
  modulesStore = inject(ModuleStore);
  userStore = inject(UserStore);
  reportStore = inject(ReportStore);
  private readonly themeService = inject(ThemeService);
  private readonly menuService = inject(MenuService);
  private readonly sidenavUi = inject(SidenavUiService);
  private readonly contextFilterService = inject(ContextFilterService);

  /** True when the rail is collapsed to icons only. */
  readonly collapsed = this.sidenavUi.collapsed;

  toggleCollapsed(): void {
    this.sidenavUi.toggle();
  }

  /**
   * Glyph standing in for a group's text header in the collapsed rail. Clicking
   * it toggles that group, so each group stays independently openable without
   * the rail having to expand.
   */
  groupIcon(groupKey: string): string {
    return BUILTIN_GROUP_ICONS[groupKey] ?? 'folder';
  }

  /**
   * Per-group open/closed state, honoured whether the rail is collapsed or not
   * — a collapsed rail shows group icons, so the user can still open exactly
   * the group they want instead of every group at once.
   */
  submenuExpanded(groupKey: string): boolean {
    return this.menuVisibility()[groupKey]?.menu === true;
  }

  submenuVisible(groupKey: string): boolean {
    return this.menuVisibility()[groupKey]?.hidden !== true;
  }

  // Resolved layered group ordering + the org's custom menus (org override ->
  // global default -> code baseline, resolved server-side).
  readonly layoutEntries = signal<MenuLayoutEntry[]>([]);
  readonly customMenus = signal<CustomMenu[]>([]);
  private readonly customExpanded = signal<Set<string>>(new Set());

  readonly logoSrc = computed(() =>
    this.themeService.activeThemeKey() === 'domaingrc-dark'
      ? '/assets/logos/domaingrc-rhino-mark-white.png'
      : '/assets/logos/domaingrc-rhino-mark-deep-navy.png'
  );

  menuVisibility = signal<MenuVisibility>({
    system: { hidden: true, menu: false, hiddenGroup: false },
    admin: { hidden: true, menu: false, hiddenGroup: false },
    report: { hidden: true, menu: false, hiddenGroup: false },
    asset: { hidden: true, menu: false, hiddenGroup: false },
    audit: { hidden: true, menu: false, hiddenGroup: false },
    domain: { hidden: true, menu: false, hiddenGroup: false },
    ai: {
      hidden: true,
      menu: false,
      hiddenGroup: false,
      chatHidden: true,
      knowledgeHidden: true,
    },
  });

  constructor() {
    // Load Reports only for callers who hold reports.view — the same gate the
    // Reports group renders under (visibleNavItems). A No-Access member with
    // module bindings never holds it (bindings carry module-scoped keys only),
    // and used to get a 403 on every page load from this one blind fetch.
    // Also reloads on org switch, which the constructor-time load never did.
    loadWhenPermitted({
      permission: PERMISSION.reports.view,
      load: () => this.reportStore.loadAll(),
    });

    // Load the resolved menu (layout + custom menus). Reload on org switch so
    // the sidenav reflects the org's override and its custom menus. switchMap
    // cancels the in-flight request on a rapid re-emit (BehaviorSubject emits
    // null at boot then the restored org) so a late first response can't
    // clobber the current org's menu.
    this.contextFilterService.organizationId$
      .pipe(
        switchMap(() => this.menuService.getResolvedMenu()),
        takeUntilDestroyed()
      )
      .subscribe((resolved) => {
        this.layoutEntries.set(resolved.entries ?? []);
        this.customMenus.set(resolved.customMenus ?? []);
      });

    // Update menu group visibility when nav items change
    effect(() => {
      const items = this.visibleNavItems();
      this.checkVisibilityMenuGroup(items);
    });
  }

  /**
   * Group-render key for a module. Placement itself is resolved by the shared
   * util (also used by the menu-config page, so the admin preview can never
   * diverge from the live nav); the bucket is then mapped onto the category
   * strings the template filters on. Orphaned custom keys (menu deleted) fall
   * back to legacy category placement instead of vanishing.
   */
  private resolveModuleGroup(module: ModuleWithModuleType): string {
    const liveCustomKeys = new Set(this.customMenus().map((menu) => menu.key));
    const bucket = resolveMenuBucket(module, liveCustomKeys);
    return mapBucketToNavCategory(bucket, module.moduleCategory);
  }

  /**
   * CSS `order` for a rendered group, from the resolved layout. Groups with no
   * explicit entry sort last (stable) so the nav never loses a group.
   */
  groupOrder(groupKey: string): number {
    const entry = this.layoutEntries().find((e) => e.groupKey === groupKey);
    return entry ? entry.weight : 999;
  }

  /** A layout entry may hide an entire group (config-driven, per layer). */
  isGroupHidden(groupKey: string): boolean {
    return (
      this.layoutEntries().find((e) => e.groupKey === groupKey)?.hidden === true
    );
  }

  // Group templates — rendered in resolved-layout order via NgTemplateOutlet so
  // the DOM order (and therefore keyboard/screen-reader traversal) matches the
  // visual order, instead of a CSS `order` illusion.
  @ViewChild('grpDefault', { static: true }) grpDefault!: TemplateRef<unknown>;
  @ViewChild('grpAi', { static: true }) grpAi!: TemplateRef<unknown>;
  @ViewChild('grpMyDomain', { static: true })
  grpMyDomain!: TemplateRef<unknown>;
  @ViewChild('grpAssetMgr', { static: true })
  grpAssetMgr!: TemplateRef<unknown>;
  @ViewChild('grpMyAudit', { static: true }) grpMyAudit!: TemplateRef<unknown>;
  @ViewChild('grpAdmin', { static: true }) grpAdmin!: TemplateRef<unknown>;
  @ViewChild('grpSystem', { static: true }) grpSystem!: TemplateRef<unknown>;
  @ViewChild('grpReports', { static: true }) grpReports!: TemplateRef<unknown>;
  @ViewChild('grpCustom', { static: true }) grpCustom!: TemplateRef<unknown>;

  groupTemplate(key: string): TemplateRef<unknown> {
    switch (key) {
      case 'default':
        return this.grpDefault;
      case 'ai':
        return this.grpAi;
      case 'myDomain':
        return this.grpMyDomain;
      case 'assetMGR':
        return this.grpAssetMgr;
      case 'myAudit':
        return this.grpMyAudit;
      case 'admin':
        return this.grpAdmin;
      case 'system':
        return this.grpSystem;
      case 'reports':
        return this.grpReports;
      default:
        return this.grpCustom;
    }
  }

  /**
   * Built-in + custom groups in resolved-layout order, hidden groups removed.
   * Custom entries carry their group data for the template's outlet context.
   */
  readonly orderedGroups = computed(
    (): Array<{ key: string; custom?: CustomMenuGroupView }> => {
      const builtin = BUILTIN_MENU_GROUP_KEYS.map((key) => ({
        key: key as string,
      }));
      const custom = this.customMenuGroups().map((group) => ({
        key: group.menu.key,
        custom: group,
      }));
      return [...builtin, ...custom]
        .filter((group) => !this.isGroupHidden(group.key))
        .sort((a, b) => this.groupOrder(a.key) - this.groupOrder(b.key));
    }
  );

  /** Custom-menu groups with their visible items, hidden when empty. */
  readonly customMenuGroups = computed(() => {
    const items = this.visibleNavItems();
    return this.customMenus()
      .map((menu) => ({
        menu,
        items: items.filter((item) => item.moduleCategory === menu.key),
        expanded: this.customExpanded().has(menu.key),
      }))
      .filter((group) => group.items.length > 0);
  });

  toggleCustomMenu(key: string) {
    const next = new Set(this.customExpanded());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.customExpanded.set(next);
  }

  isExpanded = true;
  isShowing = false;
  visibleNavItems = computed(() => this.computeVisibleNavItems());
  readonly newChatQueryParams = { new: 1 };

  /**
   * One rule per item (roles overhaul §11.2): permission-driven visibility in
   * the active org. The old role ladder, the assessor/EHS allow-lists,
   * `excludeRoles`, and `modulesWithMinOrganizationUser` are gone — a role
   * that should see less simply carries fewer permissions.
   */
  private itemVisible(item: NavigationItem): boolean {
    if (item.featureFlag !== undefined && !isFeatureEnabled(item.featureFlag)) {
      return false;
    }
    if (item.uberOnly === true) {
      return this.accessStore.isUber();
    }
    if (item.requiredPermission !== undefined) {
      return holdsRequirement(
        this.accessStore,
        item.requiredPermission,
        item.orgWide === true
      );
    }
    return true;
  }

  private computeVisibleNavItems(): NavigationItem[] {
    const navItemSet: Set<NavigationItem> = new Set();

    for (const navItem of this.navItems) {
      // SYSTEM items follow the GLOBAL uber grant, not the context org: an
      // uber who also holds a lower role in the selected org keeps /system.
      if (navItem.moduleCategory === 'SYSTEM') {
        if (this.accessStore.isUber()) {
          navItemSet.add(navItem);
        }
        continue;
      }
      if (this.itemVisible(navItem)) {
        navItemSet.add(navItem);
      }
    }

    let showUserGroupBuilder = false;
    for (const module of this.modulesStore.entities()) {
      // Per-module gate: true org-wide for base-role holders, true for THIS
      // module only via a module role binding — which is what lets a
      // No-Access member see exactly the modules they are bound into.
      if (!this.accessStore.can(PERMISSION.issues.view.own, module.id)) {
        continue;
      }
      const moduleToAdd: NavigationItem = {
        // Honours the user's saved grid-or-board default for this module.
        // Read inside `computeVisibleNavItems`, which runs in a computed, so
        // the links refresh when the map loads or a default changes.
        link: this.prefixViewPreference.linkForPrefix(module.prefix),
        text: module.name,
        iconPath: '',
        // Prefer the user-picked icon; fall back to the type-derived map, then
        // the generic one. Shared with mobile so a module looks the same in both.
        matIconName: moduleIconName(module),
        // Placement now comes from the stored menuKey; legacy rows (menuKey null)
        // keep deriving from moduleCategory. This supersedes the old hardcoded
        // controlMGR/IRLmgr → myAudit name override (backfilled to menuKey).
        moduleCategory: this.resolveModuleGroup(module),
        weight: module.weight,
      };
      const moduleExists = Array.from(navItemSet).some(
        (navItem) => navItem.link === moduleToAdd.link
      );

      if (module.moduleTypeName === ModuleTypeNameEnum.INC) {
        showUserGroupBuilder = true;
      }

      if (!moduleExists) {
        navItemSet.add(moduleToAdd);
      }
    }

    // SAVED REPORTS LOGIC (Using Report Entities)
    if (this.accessStore.can(PERMISSION.reports.view)) {
      const reports = this.reportStore.entities();
      const currentUser = this.userStore.currentUser();

      let hasFavorites = false;

      if (currentUser && currentUser.settings) {
        const settings = currentUser.settings as { favoriteReports?: string[] };
        const favorites: string[] = settings.favoriteReports || [];

        reports.forEach((report) => {
          if (favorites.includes(report.id)) {
            hasFavorites = true;
            navItemSet.add({
              link: 'reports/saved/' + report.id,
              text: report.reportName || report['name'] || 'Untitled Report',
              iconPath: '',
              matIconName: 'bookmark',
              moduleCategory: 'REPORTS',
              weight: 101,
            });
          }
        });
      }

      // Empty State Logic
      if (!hasFavorites) {
        if (reports.length > 0) {
          // Has reports, but no favorites -> "Add a favorite"
          navItemSet.add({
            link: 'reports',
            text: 'Add a favorite...',
            iconPath: '',
            matIconName: 'star_border',
            moduleCategory: 'REPORTS',
            weight: 102,
          });
        } else if (this.accessStore.canOrgWide(PERMISSION.reports.create)) {
          // No reports at all -> "Create a report", for those who may.
          navItemSet.add({
            link: 'reports',
            text: 'Create a report...',
            iconPath: '',
            matIconName: 'add_circle',
            moduleCategory: 'REPORTS',
            weight: 102,
          });
        }
      }
    }

    // User-group CRUD stays uber-only server-side until the enforcement flip
    // widens it to user-groups.manage holders.
    if (showUserGroupBuilder && this.accessStore.isUber()) {
      navItemSet.add({
        link: 'user-groups',
        text: 'User Groups',
        uberOnly: true,
        iconPath: '',
        matIconName: 'groups',
        moduleCategory: 'ADMIN',
        weight: 100,
      });
    }

    return Array.from(navItemSet).sort((a, b) => a.weight - b.weight);
  }
  navItems: NavigationItem[] = [
    {
      // Home is visible to every signed-in member — no permission required.
      link: 'homepage',
      text: 'Home',
      iconPath: '',
      matIconName: 'home',
      moduleCategory: 'DEFAULT',
      weight: 0,
    },
    {
      // Seat-gated (§8): dashboards.view only ever comes from a per-member
      // grant. Seeds gave every org/tenant admin one, so nobody lost theDash
      // at the flip; new viewers get seats from the Members screen.
      link: 'dashboard',
      text: 'theDash',
      requiredPermission: PERMISSION.dashboards.view,
      iconPath: '',
      matIconName: 'monitoring',
      moduleCategory: 'DEFAULT',
      weight: 1,
    },
    {
      // The calendar spans every module, so the key has to be held org-wide:
      // a binding into one module cannot open an org-spanning page (§7.6).
      // What appears on it is narrowed per module by issue visibility.
      link: 'calendar',
      text: 'Calendar',
      featureFlag: FEATURE_FLAGS.CALENDAR_ENABLED,
      requiredPermission: PERMISSION.calendar.view,
      orgWide: true,
      iconPath: '',
      matIconName: 'calendar_month',
      moduleCategory: 'DEFAULT',
      weight: 2,
    },
    {
      link: 'reports',
      text: 'View all reports',
      requiredPermission: PERMISSION.reports.view,
      iconPath: '',
      matIconName: 'summarize',
      moduleCategory: 'REPORTS',
      weight: 100,
    },
    {
      link: 'all-issues',
      text: 'Issue explorer',
      // The cross-module grid is the report-building surface (decided
      // 2026-09-08). `reports.create` stays ORG-WIDE — a binding must not
      // grant the right to build reports that span the org. `issues.view.all`
      // is held ANYWHERE (2026-09-14): a member bound to two modules with
      // full access there has issues to build reports over, and requiring it
      // org-wide left them holding reports.create with no surface to use it
      // on — the nav's own "Create a report…" link routed here and bounced.
      // The rows are filtered server-side by issueVisibility either way, so
      // this widens who may open the page, never what the page returns. The
      // label follows: it was never "all issues" for a bound member.
      requiredPermission: [
        { key: PERMISSION.reports.create, orgWide: true },
        { key: PERMISSION.issues.view.all, orgWide: false },
      ],
      iconPath: '',
      matIconName: 'list_alt',
      moduleCategory: 'REPORTS',
      weight: 105,
    },
    {
      // Module provisioning is a platform operation, not an org permission.
      link: 'modules',
      text: 'Modules',
      uberOnly: true,
      iconPath: '',
      matIconName: 'folder_managed',
      moduleCategory: 'ADMIN',
      weight: 10,
    },
    {
      link: 'menu-config',
      text: 'Menu Configuration',
      // Custom-menu CRUD is open to config.menu.manage holders; the reorder
      // controls inside the page are additionally gated to DomainGRC admins.
      requiredPermission: PERMISSION.config.menu.manage,
      iconPath: '',
      matIconName: 'reorder',
      moduleCategory: 'ADMIN',
      weight: 11,
    },
    {
      link: 'issue-type-manager',
      text: 'IssueType Manager',
      requiredPermission: PERMISSION.config.issueTypes.manage,
      iconPath: '',
      matIconName: 'assignment',
      moduleCategory: 'ADMIN',
      weight: 11,
    },
    {
      link: 'shared-fields',
      text: 'Shared Fields',
      requiredPermission: PERMISSION.config.issueTypes.manage,
      iconPath: '',
      matIconName: 'dataset_linked',
      moduleCategory: 'ADMIN',
      weight: 11,
    },
    {
      // Category writes stay uber-only server-side until the enforcement flip.
      link: 'issue-type-categories',
      text: 'IssueType Categories',
      uberOnly: true,
      iconPath: '',
      matIconName: 'category',
      moduleCategory: 'ADMIN',
      weight: 12,
    },
    {
      link: 'system/users',
      text: 'System Users',
      iconPath: '',
      matIconName: 'manage_accounts',
      moduleCategory: 'SYSTEM',
      weight: 1,
    },
    {
      link: 'system/roles',
      text: 'Roles',
      iconPath: '',
      matIconName: 'shield_person',
      moduleCategory: 'SYSTEM',
      weight: 2,
    },
    {
      link: 'system/licenses',
      text: 'Licenses',
      iconPath: '',
      matIconName: 'workspace_premium',
      moduleCategory: 'SYSTEM',
      weight: 3,
    },
    {
      link: 'system/tenants',
      text: 'Tenants',
      iconPath: '',
      matIconName: 'apartment',
      moduleCategory: 'SYSTEM',
      weight: 13,
    },
    {
      // The org profile lives under Admin: it is the settings surface for
      // org.settings.manage holders, not a read-only page for every member.
      // Ungated it was the one ADMIN item a Member passed, which made the
      // whole Admin section render for them.
      link: 'organization',
      text: 'Organization',
      requiredPermission: PERMISSION.org.settings.manage,
      iconPath: '',
      matIconName: 'corporate_fare',
      moduleCategory: 'ADMIN',
      weight: 4,
    },
    {
      link: 'system/organizations',
      text: 'Manage Organizations',
      iconPath: '',
      matIconName: 'domain_add',
      moduleCategory: 'SYSTEM',
      weight: 14,
    },
    {
      link: 'users',
      text: 'Members',
      requiredPermission: PERMISSION.members.view,
      iconPath: '',
      matIconName: 'group',
      moduleCategory: 'ADMIN',
      weight: 5,
    },
    {
      link: 'admin/roles',
      text: 'Roles',
      requiredPermission: PERMISSION.roles.manage,
      iconPath: '',
      matIconName: 'shield_person',
      moduleCategory: 'ADMIN',
      weight: 6,
    },
    {
      link: 'admin/calendars',
      text: 'Calendars',
      featureFlag: FEATURE_FLAGS.CALENDAR_ENABLED,
      requiredPermission: PERMISSION.calendar.manage,
      orgWide: true,
      iconPath: '',
      matIconName: 'edit_calendar',
      moduleCategory: 'ADMIN',
      weight: 7,
    },
    {
      link: 'system/audit',
      text: 'Audit History',
      iconPath: '',
      matIconName: 'history',
      moduleCategory: 'SYSTEM',
      weight: 6,
    },
    {
      // No dedicated import permission in the catalog yet — org-admin proxy,
      // matching today's gate (see the /history/import route).
      link: 'history/import',
      text: 'Import History',
      requiredPermission: PERMISSION.org.settings.manage,
      iconPath: '',
      matIconName: 'import_export',
      moduleCategory: 'ADMIN',
      weight: 14,
    },
    {
      link: 'admin/module-type-configuration',
      text: 'Module Types',
      uberOnly: true,
      iconPath: '',
      matIconName: 'settings_input_component',
      moduleCategory: 'ADMIN',
      weight: 80,
    },
    {
      link: 'admin/tab-labels',
      text: 'Tab Labels',
      requiredPermission: PERMISSION.config.menu.manage,
      iconPath: '',
      matIconName: 'label',
      moduleCategory: 'ADMIN',
      weight: 80,
    },
    {
      link: 'admin/search-config',
      text: 'Search Config',
      uberOnly: true,
      iconPath: '',
      matIconName: 'manage_search',
      moduleCategory: 'ADMIN',
      weight: 81,
    },
    {
      link: 'admin/dashboard-config',
      text: 'Dashboard Config',
      uberOnly: true,
      iconPath: '',
      matIconName: 'dashboard_customize',
      moduleCategory: 'ADMIN',
      weight: 82,
    },
    {
      link: 'admin/integrations',
      text: 'Integrations',
      requiredPermission: PERMISSION.config.integrations.manage,
      iconPath: '',
      matIconName: 'hub',
      moduleCategory: 'ADMIN',
      weight: 83,
    },
    {
      link: 'admin/automations',
      text: 'Automations',
      requiredPermission: PERMISSION.config.automations.manage,
      iconPath: '',
      matIconName: 'account_tree',
      moduleCategory: 'ADMIN',
      weight: 84,
    },
    {
      link: 'admin/contacts',
      text: 'Contacts',
      requiredPermission: PERMISSION.contacts.manage,
      iconPath: '',
      matIconName: 'contacts',
      moduleCategory: 'ADMIN',
      weight: 85,
    },
    {
      link: 'system/share-links',
      text: 'Share Links',
      iconPath: '',
      matIconName: 'qr_code_2',
      moduleCategory: 'SYSTEM',
      weight: 85,
    },
    {
      link: 'system/clone-organization',
      text: 'Clone Organization',
      iconPath: '',
      matIconName: 'content_copy',
      moduleCategory: 'SYSTEM',
      weight: 86,
    },
    {
      link: 'attachments',
      text: 'artifactMGR',
      // The library lists files across every module, so a binding into one
      // module is not enough — the permission has to be held org-wide.
      requiredPermission: PERMISSION.issues.view.own,
      orgWide: true,
      iconPath: '',
      matIconName: 'attach_file',
      moduleCategory: 'AUDIT',
      weight: 100,
    },
  ];

  mouseenter() {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }

  mouseleave() {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }
  toggleVisibility(menuType: string) {
    const current = this.menuVisibility();
    const menu = current[menuType];
    if (menu) {
      this.menuVisibility.set({
        ...current,
        [menuType]: { ...menu, hidden: !menu.hidden, menu: !menu.menu },
      });
    } else {
      console.error('Invalid menu type');
    }
  }

  checkVisibilityMenuGroup(navItems: NavigationItem[]) {
    const allowedModuleCategories = navItems.map(
      (visibleNavItem) => visibleNavItem.moduleCategory
    );
    const current = untracked(this.menuVisibility);

    const newAssetHidden = !allowedModuleCategories.includes('ASSET');
    const newAuditHidden = !allowedModuleCategories.includes('AUDIT');
    const newAdminHidden = !allowedModuleCategories.includes('ADMIN');
    const newReportHidden = !allowedModuleCategories.includes('REPORTS');
    // SYSTEM items only enter visibleNavItems for global ubers, so this
    // mirrors that gate without re-deriving it.
    const newSystemHidden = !allowedModuleCategories.includes('SYSTEM');
    // AI gating: EVERY item under the AI module — chat, history, and the
    // Knowledge Base alike — requires the ai_module_enabled feature flag AND
    // the ai.access entitlement (§9.2; ubers pass through can()). One gate,
    // so the section header and its submenu items appear and disappear
    // together, and so the menu matches `aiModuleGuard` on the routes (a
    // hidden item is never a reachable URL).
    const aiModuleEnabled = isFeatureEnabled(FEATURE_FLAGS.AI_MODULE_ENABLED);
    const newAiHidden =
      !aiModuleEnabled || !this.accessStore.can(PERMISSION.ai.access);
    const aiChatHidden = newAiHidden;
    const aiKnowledgeHidden = newAiHidden;

    // Only write if hiddenGroup values actually changed to avoid unnecessary change detection
    if (
      current['asset'].hiddenGroup === newAssetHidden &&
      current['audit'].hiddenGroup === newAuditHidden &&
      current['admin'].hiddenGroup === newAdminHidden &&
      current['system'].hiddenGroup === newSystemHidden &&
      current['report'].hiddenGroup === newReportHidden &&
      current['ai'].hiddenGroup === newAiHidden &&
      current['ai'].chatHidden === aiChatHidden &&
      current['ai'].knowledgeHidden === aiKnowledgeHidden
    ) {
      return;
    }

    this.menuVisibility.set({
      ...current,
      asset: { ...current['asset'], hiddenGroup: newAssetHidden },
      audit: { ...current['audit'], hiddenGroup: newAuditHidden },
      admin: { ...current['admin'], hiddenGroup: newAdminHidden },
      system: { ...current['system'], hiddenGroup: newSystemHidden },
      report: { ...current['report'], hiddenGroup: newReportHidden },
      ai: {
        ...current['ai'],
        hiddenGroup: newAiHidden,
        chatHidden: aiChatHidden,
        knowledgeHidden: aiKnowledgeHidden,
      },
    });
  }

  removeFavorite(item: NavigationItem, event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const currentUser = this.userStore.currentUser();
    if (!currentUser) return;

    const reportId = item.link.split('/').pop();
    if (!reportId) return;

    const settings =
      (currentUser.settings as { favoriteReports?: string[] } | null) || {};
    const favorites = settings.favoriteReports || [];

    const newFavorites = favorites.filter((id) => id !== reportId);

    this.userStore
      .editCurrentUser(currentUser.id, {
        settings: { ...settings, favoriteReports: newFavorites },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
